import { createAsyncThunk } from '@reduxjs/toolkit';
import { errMsg } from '../utils/errMsg';
import {
    setNotifications,
    appendNotifications,
    setUnreadCount,
    setLoading,
    setLoadingMore,
    setSettings,
    setSettingsLoading,
    markRead,
    markAllRead,
    removeNotification,
    setError,
} from '../slices/notificationsSlice';

import { refreshAndRetry } from '../utils/refreshAndRetry';
import { track } from '../../utils/analytics';

export const NOTIFICATIONS_PAGE_SIZE = 20;

// page 1 replaces the list; page > 1 appends (infinite scroll). The server
// (user-library-service) pages with LIMIT/OFFSET and caps limit at 200.
export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async ({ page = 1, limit = NOTIFICATIONS_PAGE_SIZE } = {}, { dispatch }) => {
        const append = page > 1;
        try {
            dispatch(append ? setLoadingMore(true) : setLoading(true));

            const result = await refreshAndRetry(
                (token) => window.electron.library.getNotifications(token, page, limit)
            );

            if (result.error) {
                dispatch(setError(errMsg(result.error)));
                return;
            }

            const data = result.data || [];
            const total = result.pagination?.total || 0;

            dispatch(append
                ? appendNotifications({ data, total, page })
                : setNotifications({ data, total, page }));
            dispatch(setError(null));
        } catch (error) {
            console.error('Error fetching notifications:', error);
            dispatch(setError(errMsg(error)));
        } finally {
            dispatch(append ? setLoadingMore(false) : setLoading(false));
        }
    }
);

// Next page for infinite scroll. No-op while a fetch is in flight or when
// the server has nothing more — safe to call from an IntersectionObserver.
export const fetchMoreNotifications = createAsyncThunk(
    'notifications/fetchMoreNotifications',
    async (_, { dispatch, getState }) => {
        const { page, hasMore, loading, loadingMore } = getState().notifications;
        if (!hasMore || loading || loadingMore) return;
        track('notifications_scrolled_more', { page: page + 1 });
        await dispatch(fetchNotifications({ page: page + 1 }));
    }
);

export const fetchUnreadCount = createAsyncThunk(
    'notifications/fetchUnreadCount',
    async (_, { dispatch }) => {
        try {
            const result = await refreshAndRetry(
                (token) => window.electron.library.getNotificationUnreadCount(token)
            );

            if (result.error) {
                console.error('Fetch unread count failed:', result.error);
                return;
            }

            const count = result?.data?.count ?? result?.count ?? 0;
            dispatch(setUnreadCount(count));
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    }
);

export const fetchNotificationSettings = createAsyncThunk(
    'notifications/fetchNotificationSettings',
    async (_, { dispatch }) => {
        try {
            dispatch(setSettingsLoading(true));

            const result = await refreshAndRetry(
                (token) => window.electron.library.getNotificationSettings(token)
            );

            if (result.error) {
                console.error('Fetch notification settings failed:', result.error);
                return;
            }

            if (result.data) {
                dispatch(setSettings(mapServerSettings(result.data)));
            }
        } catch (error) {
            console.error('Error fetching notification settings:', error);
        } finally {
            dispatch(setSettingsLoading(false));
        }
    }
);

const mapServerSettings = (data) => ({
    likedTrackPlayed: data.liked_track_played ?? data.likedTrackPlayed ?? true,
    favouriteArtistNewMix: data.favourite_artist_new_mix ?? data.favouriteArtistNewMix ?? true,
    favouriteArtistTrackPlayed: data.favourite_artist_track_played ?? data.favouriteArtistTrackPlayed ?? true,
    emailLikedTrackPlayed: data.email_liked_track_played ?? data.emailLikedTrackPlayed ?? false,
    emailFavouriteArtistNewMix: data.email_favourite_artist_new_mix ?? data.emailFavouriteArtistNewMix ?? false,
    emailFavouriteArtistTrackPlayed: data.email_favourite_artist_track_played ?? data.emailFavouriteArtistTrackPlayed ?? false,
    // Recency window for followed-artist new-mix: null = any mix (default).
    // null is a real value here, so ?? must not swallow it.
    newMixWindowDays: data.new_mix_window_days !== undefined
        ? data.new_mix_window_days
        : (data.newMixWindowDays ?? null),
});

export const updateNotificationSettings = createAsyncThunk(
    'notifications/updateNotificationSettings',
    async (settings, { dispatch }) => {
        try {
            // Optimistic update
            dispatch(setSettings(settings));

            const result = await refreshAndRetry(
                (token) => window.electron.library.updateNotificationSettings(token, settings)
            );

            if (result.error) {
                console.error('Update notification settings failed:', result.error);
            } else {
                track('notification_settings_changed', { ...settings });
            }
            // Don't overwrite optimistic update with server response
        } catch (error) {
            console.error('Error updating notification settings:', error);
        }
    }
);

export const markNotificationAsRead = createAsyncThunk(
    'notifications/markNotificationAsRead',
    async (notificationId, { dispatch }) => {
        try {
            // Optimistic update
            dispatch(markRead(notificationId));

            const result = await refreshAndRetry(
                (token) => window.electron.library.markNotificationRead(token, notificationId)
            );

            if (result.error) {
                console.error('Mark notification read failed:', result.error);
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }
);

export const markAllNotificationsAsRead = createAsyncThunk(
    'notifications/markAllNotificationsAsRead',
    async (_, { dispatch }) => {
        try {
            // Optimistic update
            dispatch(markAllRead());

            const result = await refreshAndRetry(
                (token) => window.electron.library.markAllNotificationsRead(token)
            );

            if (result.error) {
                console.error('Mark all notifications read failed:', result.error);
            }
            // Re-sync the badge from the server AFTER the PATCH landed (ordered,
            // so it can't race the optimistic 0 above and re-show a stale count).
            dispatch(fetchUnreadCount());
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }
);

export const deleteNotification = createAsyncThunk(
    'notifications/deleteNotification',
    async (notificationId, { dispatch }) => {
        try {
            dispatch(removeNotification(notificationId));
            const result = await refreshAndRetry(
                (token) => window.electron.library.deleteNotification(token, notificationId)
            );
            if (result.error) {
                console.error('Delete notification failed:', result.error);
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    }
);

export const deleteAllNotifications = createAsyncThunk(
    'notifications/deleteAllNotifications',
    async (_, { dispatch }) => {
        try {
            dispatch(setNotifications({ data: [], total: 0 }));
            dispatch(setUnreadCount(0));
            const result = await refreshAndRetry(
                (token) => window.electron.library.deleteAllNotifications(token)
            );
            if (result.error) {
                console.error('Delete all notifications failed:', result.error);
            }
        } catch (error) {
            console.error('Error deleting all notifications:', error);
        }
    }
);
